// src/components/VisitorContactWidget/components/VisitorChatPanel.jsx

import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import {
  FiLoader,
  FiMail,
  FiMessageCircle,
  FiSend,
  FiUser,
  FiUsers,
} from "react-icons/fi";

function FormField({
  icon: Icon,
  label,
  required = false,
  children,
}) {
  return (
    <label className="block">
      <span className="mb-1.5 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.1em] text-slate-500">
        {Icon && <Icon />}
        {label}
        {required && (
          <span className="text-red-500">*</span>
        )}
      </span>

      {children}
    </label>
  );
}

function MessageBubble({ message }) {
  const isAdmin = message.sender_type === "admin";
  const isSystem = message.sender_type === "system";

  if (isSystem) {
    return (
      <div className="my-3 text-center text-xs text-slate-400">
        {message.message}
      </div>
    );
  }

  return (
    <div
      className={`flex ${
        isAdmin ? "justify-start" : "justify-end"
      }`}
    >
      <div
        className={`max-w-[86%] rounded-2xl px-4 py-3 text-sm leading-6 ${
          isAdmin
            ? "rounded-bl-md bg-slate-100 text-slate-700"
            : "rounded-br-md bg-[#123B57] text-white"
        }`}
      >
        <div className="whitespace-pre-wrap break-words">
          {message.message}
        </div>

        <div
          className={`mt-1.5 text-[10px] ${
            isAdmin
              ? "text-slate-400"
              : "text-white/55"
          }`}
        >
          {new Date(
            message.created_at,
          ).toLocaleTimeString("fr-FR", {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
      </div>
    </div>
  );
}

export default function VisitorChatPanel({
  conversationId,
  messages,
  loadingConversation,
  submitting,
  errorMessage,
  storedContact,
  onStartConversation,
  onSendMessage,
}) {
  const [form, setForm] = useState({
    name: storedContact?.name || "",
    email: storedContact?.email || "",
    organization_name:
      storedContact?.organization_name || "",
    subject: "",
    message: "",
  });

  const [newMessage, setNewMessage] = useState("");

  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  }, [messages.length]);

  const handleFormChange = (event) => {
    const { name, value } = event.target;

    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleStartConversation = async (event) => {
    event.preventDefault();

    if (
      !form.name.trim() ||
      !form.email.trim() ||
      !form.message.trim()
    ) {
      return;
    }

    await onStartConversation({
      ...form,
      name: form.name.trim(),
      email: form.email.trim(),
      organization_name:
        form.organization_name.trim(),
      subject: form.subject.trim(),
      message: form.message.trim(),
    });
  };

  const handleSendMessage = async (event) => {
    event.preventDefault();

    if (!newMessage.trim()) return;

    const sent = await onSendMessage(
      newMessage.trim(),
    );

    if (sent) {
      setNewMessage("");
    }
  };

  if (!conversationId) {
    return (
      <form
        onSubmit={handleStartConversation}
        className="space-y-4 p-5"
      >
        <div className="rounded-2xl bg-[#EEF7FC] p-4 text-sm leading-6 text-slate-600">
          Présentez-vous rapidement et laissez-moi votre
          question. Vous pourrez ensuite suivre mes réponses
          directement dans cette fenêtre.
        </div>

        <FormField
          icon={FiUser}
          label="Votre prénom et nom"
          required
        >
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleFormChange}
            required
            autoComplete="name"
            placeholder="Jean Dupont"
            className="w-full rounded-xl border border-slate-200 px-3.5 py-3 text-sm outline-none transition focus:border-[#2EA7E0] focus:ring-2 focus:ring-[#2EA7E0]/10"
          />
        </FormField>

        <FormField
          icon={FiMail}
          label="Votre email"
          required
        >
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleFormChange}
            required
            autoComplete="email"
            placeholder="contact@monclub.fr"
            className="w-full rounded-xl border border-slate-200 px-3.5 py-3 text-sm outline-none transition focus:border-[#2EA7E0] focus:ring-2 focus:ring-[#2EA7E0]/10"
          />
        </FormField>

        <FormField
          icon={FiUsers}
          label="Club ou association"
        >
          <input
            type="text"
            name="organization_name"
            value={form.organization_name}
            onChange={handleFormChange}
            placeholder="Nom de votre structure"
            className="w-full rounded-xl border border-slate-200 px-3.5 py-3 text-sm outline-none transition focus:border-[#2EA7E0]"
          />
        </FormField>

        <FormField
          icon={FiMessageCircle}
          label="Votre question"
          required
        >
          <textarea
            name="message"
            value={form.message}
            onChange={handleFormChange}
            required
            rows={5}
            placeholder="Comment puis-je vous aider ?"
            className="w-full resize-none rounded-xl border border-slate-200 px-3.5 py-3 text-sm outline-none transition focus:border-[#2EA7E0]"
          />
        </FormField>

        {errorMessage && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {errorMessage}
          </div>
        )}

        <button
          type="submit"
          disabled={
            submitting ||
            !form.name.trim() ||
            !form.email.trim() ||
            !form.message.trim()
          }
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#123B57] px-4 py-3.5 text-sm font-extrabold text-white transition hover:bg-[#1C567F] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? (
            <>
              <FiLoader className="animate-spin" />
              Envoi...
            </>
          ) : (
            <>
              <FiSend />
              Envoyer ma question
            </>
          )}
        </button>
      </form>
    );
  }

  return (
    <div className="flex h-[540px] flex-col">
      <div className="flex items-center gap-3 border-b border-slate-100 bg-emerald-50 px-4 py-3">
        <span className="relative flex h-3 w-3">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-50" />
          <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500" />
        </span>

        <div>
          <div className="text-xs font-bold text-emerald-700">
            Conversation ouverte
          </div>

          <div className="text-[11px] text-emerald-600">
            Je répondrai dès que possible.
          </div>
        </div>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto bg-white p-4">
        {loadingConversation && !messages.length ? (
          <div className="flex h-full items-center justify-center text-sm text-slate-400">
            <FiLoader className="mr-2 animate-spin" />
            Chargement de la conversation...
          </div>
        ) : messages.length ? (
          messages.map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
            />
          ))
        ) : (
          <div className="flex h-full flex-col items-center justify-center text-center text-slate-400">
            <FiMessageCircle className="text-3xl" />

            <p className="mt-3 text-sm">
              Aucun message pour le moment.
            </p>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {errorMessage && (
        <div className="border-t border-red-100 bg-red-50 px-4 py-2 text-xs text-red-700">
          {errorMessage}
        </div>
      )}

      <form
        onSubmit={handleSendMessage}
        className="border-t border-slate-100 bg-white p-3"
      >
        <div className="flex items-end gap-2">
          <textarea
            value={newMessage}
            onChange={(event) =>
              setNewMessage(event.target.value)
            }
            onKeyDown={(event) => {
              if (
                event.key === "Enter" &&
                !event.shiftKey
              ) {
                event.preventDefault();
                handleSendMessage(event);
              }
            }}
            rows={2}
            placeholder="Écrire un message..."
            className="max-h-28 min-h-[48px] flex-1 resize-none rounded-xl border border-slate-200 px-3.5 py-3 text-sm outline-none focus:border-[#2EA7E0]"
          />

          <button
            type="submit"
            disabled={
              submitting || !newMessage.trim()
            }
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#123B57] text-white transition hover:bg-[#1C567F] disabled:opacity-40"
          >
            {submitting ? (
              <FiLoader className="animate-spin" />
            ) : (
              <FiSend />
            )}
          </button>
        </div>
      </form>
    </div>
  );
}